import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Calendar,
  Check,
  Mail,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Switch } from "../../../components/ui/switch";
import { cn } from "../../../lib/utils";
import {
  useEmailDomainReview,
  useEmailSettings,
  useMemberIntegrationsMe,
  useUpdateEmailSettings,
} from "../../../hooks/QueryHooks";
import useObjectSearchTable from "../../../shared/table/useObjectSearchTable";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import type {
  EmailDomainReviewList,
  EmailSettings,
} from "../../../services/emailSettingsEndpoint";

// Membrane / member-integration provider keys for Google email + calendar.
// Tolerate both underscore and hyphen spellings across the stack.
const GMAIL_PROVIDERS = ["gmail"];
const CALENDAR_PROVIDERS = ["google_calendar", "google-calendar"];

const hasConnectedProvider = (
  integrations: any[] | undefined,
  providers: string[],
) => {
  if (!Array.isArray(integrations)) return false;
  return integrations.some((item) => {
    const provider = String(item?.provider || "").toLowerCase().trim();
    const active = item?.is_active ?? true;
    return active && providers.includes(provider);
  });
};

type StepCardProps = {
  index: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  done: boolean;
  children?: React.ReactNode;
};

const StepCard = ({
  index,
  icon: Icon,
  title,
  description,
  done,
  children,
}: StepCardProps) => (
  <Card className={cn("transition-colors", done && "border-status-active/40")}>
    <CardHeader className="flex flex-row items-start gap-3 space-y-0">
      <div
        className={cn(
          "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border text-[13px] font-medium",
          done
            ? "border-status-active/40 bg-status-active/10 text-status-active"
            : "border-border bg-muted text-muted-foreground",
        )}
      >
        {done ? <Check className="h-4 w-4" /> : index}
      </div>
      <div className="min-w-0 flex-1">
        <CardTitle className="flex items-center gap-2 text-[14px] font-medium text-foreground">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        <CardDescription className="mt-1 text-[12px] text-muted-foreground">
          {description}
        </CardDescription>
      </div>
    </CardHeader>
    {children ? <CardContent className="pl-[60px]">{children}</CardContent> : null}
  </Card>
);

const EmailFirstGettingStarted = () => {
  const navigate = useNavigate();
  const { workspaceId: workspaceSlug } = useParams<{ workspaceId: string }>();
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id;

  useEffect(() => {
    document.title = "Get started | Hyperengage";
  }, []);

  // --- Email / calendar connection state -------------------------------
  const memberIntegrationsQuery = useMemberIntegrationsMe({ workspaceId });
  const memberIntegrations = memberIntegrationsQuery.data as any[] | undefined;
  const gmailConnected = hasConnectedProvider(memberIntegrations, GMAIL_PROVIDERS);
  const calendarConnected = hasConnectedProvider(memberIntegrations, CALENDAR_PROVIDERS);

  // --- Email settings (create_from_email) ------------------------------
  const emailSettingsQuery = useEmailSettings({ workspaceId });
  const settings = emailSettingsQuery.data as EmailSettings | undefined;
  const updateMutation = useUpdateEmailSettings();

  const [createFromEmail, setCreateFromEmail] = useState(false);
  useEffect(() => {
    if (settings) setCreateFromEmail(Boolean(settings.create_from_email));
  }, [settings]);

  const handleToggleCreateFromEmail = (next: boolean) => {
    if (!workspaceId) return;
    setCreateFromEmail(next); // optimistic
    updateMutation.mutate(
      { workspace_id: workspaceId, create_from_email: next },
      {
        onError: () => setCreateFromEmail((prev) => !prev),
      },
    );
  };

  // --- Book of business counts -----------------------------------------
  const accountsTable = useObjectSearchTable({
    workspaceId,
    entityType: "accounts",
    initialPageSize: 1,
    queryOptions: {},
  });
  const contactsTable = useObjectSearchTable({
    workspaceId,
    entityType: "users",
    initialPageSize: 1,
    queryOptions: {},
  });
  const accountsCount = accountsTable.total;
  const contactsCount = contactsTable.total;
  const countsLoading = accountsTable.isLoading || contactsTable.isLoading;

  // --- Flagged domains -------------------------------------------------
  const domainReviewQuery = useEmailDomainReview({ workspaceId, status: "pending" });
  const domainReviewData = domainReviewQuery.data as EmailDomainReviewList | undefined;
  const pendingDomains = domainReviewData?.pending_count ?? 0;

  const completedCount = useMemo(() => {
    let count = 0;
    if (gmailConnected) count += 1;
    if (createFromEmail) count += 1;
    return count;
  }, [gmailConnected, createFromEmail]);

  const goToIntegrations = () =>
    navigate(`/home/${workspaceSlug}/settings/integrations`);
  const goToEmailSettings = () =>
    navigate(`/home/${workspaceSlug}/settings/email-settings`);
  const goToDomainReview = () =>
    navigate(`/home/${workspaceSlug}/settings/domain-review`);
  const goToAccounts = () => navigate(`/home/${workspaceSlug}/headquarters`);

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-6 py-8">
      <div>
        <h1 className="text-lg font-medium text-foreground">Get started with email-first</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Connect your inbox and Hyperengage will build your book of business
          automatically from the customers you already email.
        </p>
        <p className="mt-2 text-[12px] text-muted-foreground">
          {completedCount} of 2 core steps complete
        </p>
      </div>

      <div className="space-y-4">
        {/* (a) Connect Gmail */}
        <StepCard
          index={1}
          icon={Mail}
          title="Connect your email (Gmail)"
          description="Sync your inbox so Hyperengage can detect customers and contacts from your conversations."
          done={gmailConnected}
        >
          <div className="flex items-center gap-3">
            {gmailConnected ? (
              <Badge variant="secondary" className="h-6 gap-1 px-2 text-[11px]">
                <Check className="h-3 w-3" /> Connected
              </Badge>
            ) : (
              <Button
                size="sm"
                className="h-8 text-[12px]"
                onClick={goToIntegrations}
                disabled={memberIntegrationsQuery.isLoading}
              >
                Connect Gmail
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            )}
            {!gmailConnected ? (
              <span className="text-[12px] text-muted-foreground">
                Opens Settings → Integrations
              </span>
            ) : null}
          </div>
        </StepCard>

        {/* (b) Connect calendar (optional) */}
        <StepCard
          index={2}
          icon={Calendar}
          title="Connect calendar (optional)"
          description="Add Google Calendar for meeting context and richer account timelines."
          done={calendarConnected}
        >
          <div className="flex items-center gap-3">
            {calendarConnected ? (
              <Badge variant="secondary" className="h-6 gap-1 px-2 text-[11px]">
                <Check className="h-3 w-3" /> Connected
              </Badge>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-[12px]"
                onClick={goToIntegrations}
                disabled={memberIntegrationsQuery.isLoading}
              >
                Connect calendar
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </StepCard>

        {/* (c) Enable email-first account creation */}
        <StepCard
          index={3}
          icon={Building2}
          title="Enable email-first account creation"
          description="Let Hyperengage create accounts and contacts automatically from inbound email."
          done={createFromEmail}
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <span className="text-[13px] font-medium text-foreground">
                Create accounts from email
              </span>
              <Switch
                checked={createFromEmail}
                onCheckedChange={handleToggleCreateFromEmail}
                disabled={!workspaceId || updateMutation.isLoading || emailSettingsQuery.isLoading}
              />
            </div>
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <p className="text-[12px] leading-[18px] text-muted-foreground">
                We skip HR/legal/investor/personal threads and never create accounts
                from free-mail or your internal domains; sensitive threads are dropped.
              </p>
            </div>
            <button
              type="button"
              onClick={goToEmailSettings}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline"
            >
              Advanced email settings
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </StepCard>

        {/* (d) Your book of business */}
        <StepCard
          index={4}
          icon={Users}
          title="Your book of business"
          description="Accounts and contacts detected from your synced inbox."
          done={accountsCount > 0}
        >
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-[13px] text-foreground">
              {countsLoading ? (
                <span className="text-muted-foreground">Counting…</span>
              ) : (
                <span>
                  <span className="font-semibold">{accountsCount.toLocaleString()}</span>{" "}
                  accounts ·{" "}
                  <span className="font-semibold">{contactsCount.toLocaleString()}</span>{" "}
                  contacts detected
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-[12px]"
              onClick={goToAccounts}
            >
              View accounts
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </StepCard>

        {/* (e) Review flagged domains */}
        <StepCard
          index={5}
          icon={ShieldCheck}
          title="Review flagged domains"
          description="Ambiguous domains seen in your inbox that need a quick allow/block decision."
          done={!domainReviewQuery.isLoading && pendingDomains === 0}
        >
          <div className="flex flex-wrap items-center gap-3">
            {domainReviewQuery.isLoading ? (
              <span className="text-[13px] text-muted-foreground">Loading…</span>
            ) : pendingDomains > 0 ? (
              <Badge variant="secondary" className="h-6 px-2 text-[11px]">
                {pendingDomains} pending
              </Badge>
            ) : (
              <span className="text-[13px] text-muted-foreground">Nothing to review</span>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-[12px]"
              onClick={goToDomainReview}
            >
              Review domains
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </StepCard>
      </div>
    </div>
  );
};

export default EmailFirstGettingStarted;
